import cloneDeep from 'clone-deep'
import { useEffect, useState } from 'react'
import { Modal } from 'stories/components'
import { InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from './constants'
import { IIntegrationChannel } from './type'

/**
 * Add/edit form for a Slack/Teams channel. `onClose(payload)` receives the channel fields on
 * submit, or `null` when cancelled. The parent owns persistence and refetch.
 */
export const AddSlackTeamsChannelModal = ({
  data,
  onClose,
}: {
  data: IIntegrationChannel | null
  onClose: Function
}) => {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs())

  useEffect(() => {
    const newInputs = cloneDeep(inputs)
    if (data) {
      newInputs.name.value = data.name
      newInputs.platform.value = data.platform
      newInputs.webhookUrl.value = data.webhookUrl
      newInputs.events.value = (data.events || []).reduce(
        (acc: Record<string, boolean>, key) => ({ ...acc, [key]: true }),
        {},
      )
      newInputs.enabled.value = data.enabled
    } else {
      newInputs.platform.value = 'slack'
      newInputs.enabled.value = true
    }
    setInputs(newInputs)
  }, [])

  const onChange = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = value
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const onSubmit = async () => {
    const newInputs = cloneDeep(inputs)
    const values: Record<string, any> = {}
    let hasError = false
    Object.keys(inputs).forEach((key) => {
      newInputs[key].error = InputValidate(newInputs[key])
      const { value, error } = newInputs[key]
      if (error) hasError = true
      values[key] = value
    })
    setInputs(newInputs)

    const events = Object.keys((values.events as Record<string, boolean>) || {}).filter((k) => values.events[k])
    if (!events.length) {
      newInputs.events.error = 'Select at least one event'
      setInputs(newInputs)
      hasError = true
    }
    if (hasError) return false

    setLoading(true)
    await onClose({
      name: values.name,
      platform: values.platform,
      webhookUrl: values.webhookUrl,
      events,
      enabled: values.enabled !== false,
    })
    setLoading(false)
  }

  return (
    <Modal
      isOpen
      loading={isLoading}
      title={`${data ? 'Update' : 'Add'} Channel`}
      titleOkay="Submit"
      onClose={() => onClose(null)}
      onOk={onSubmit}
    >
      <div className="w-128">
        {Object.keys(inputs).map((key) => {
          const input = inputs[key]
          if (input.visible === false) return null

          return (
            <div className="mb-4" key={key}>
              <RenderInput Key={key} input={input} onChange={onChange} />
            </div>
          )
        })}
      </div>
    </Modal>
  )
}
